import React, { useMemo, useState } from 'react';
import { useMood } from '../../contexts/MoodContext';
import { getMood } from './moods';
import {
  addDays, dayKey, fmtDayNum, fmtLongDay, fmtWeekday, fmtWeekRange, startOfWeek, weekDays, weekKey,
} from './moodDates';
import { MoodDonut, MoodBars, summarySentence } from './MoodCharts';
import CheckInList from './CheckInList';
import ReflectionPanel from './ReflectionPanel';

/**
 * WeeklyView — a seven-day journal. Navigate weeks, see each day's recorded moods
 * and counts, open a day for its entries, and read a summary computed only from
 * what was actually recorded. Days with nothing simply show as empty — never a
 * mood assumed on the user's behalf.
 */
export const WeeklyView: React.FC = () => {
  const { checkIns } = useMood();
  const [anchor, setAnchor] = useState(new Date());
  const [selectedKey, setSelectedKey] = useState<string | null>(dayKey(new Date()));

  const days = useMemo(() => weekDays(anchor), [anchor]);

  const byDay = useMemo(() => {
    const map = new Map<string, typeof checkIns>();
    checkIns.forEach((c) => {
      const k = dayKey(new Date(c.createdAt));
      (map.get(k) ?? map.set(k, []).get(k)!).push(c);
    });
    return map;
  }, [checkIns]);

  const weekRecords = useMemo(
    () => days.flatMap((d) => byDay.get(dayKey(d)) ?? []),
    [days, byDay]
  );

  const canGoNext = startOfWeek(anchor).getTime() < startOfWeek(new Date()).getTime();
  const selectedRecords = selectedKey ? byDay.get(selectedKey) ?? [] : [];
  const selectedDate = selectedKey ? new Date(`${selectedKey}T00:00:00`) : null;

  return (
    <div className="mm-view">
      {/* Period navigation */}
      <div className="mm-navbar">
        <button type="button" className="mm-nav-arrow" aria-label="Previous week" onClick={() => setAnchor((a) => addDays(a, -7))}>‹</button>
        <div className="mm-navbar__label">
          <strong>{fmtWeekRange(anchor)}</strong>
          <button type="button" className="mm-link-btn" onClick={() => { setAnchor(new Date()); setSelectedKey(dayKey(new Date())); }}>This week</button>
        </div>
        <button type="button" className="mm-nav-arrow" aria-label="Next week" disabled={!canGoNext} onClick={() => setAnchor((a) => addDays(a, 7))}>›</button>
      </div>

      {/* Seven-day timeline */}
      <section className="mm-panel">
        <div className="mm-week">
          {days.map((d) => {
            const k = dayKey(d);
            const recs = byDay.get(k) ?? [];
            const top = recs[0] ? getMood(recs[0].mood) : undefined;
            const isSel = k === selectedKey;
            const isFuture = d.getTime() > Date.now();
            return (
              <button
                key={k}
                type="button"
                className={`mm-day ${isSel ? 'is-selected' : ''} ${recs.length ? 'has-data' : ''}`}
                style={top ? { ['--mood-accent' as string]: top.accent } : undefined}
                onClick={() => setSelectedKey(k)}
                disabled={isFuture && recs.length === 0}
                aria-label={`${fmtLongDay(d)}, ${recs.length} check-in${recs.length === 1 ? '' : 's'}`}
              >
                <span className="mm-day__wd">{fmtWeekday(d)}</span>
                <span className="mm-day__num">{fmtDayNum(d)}</span>
                <span className="mm-day__dots" aria-hidden="true">
                  {recs.slice(0, 3).map((r) => <span key={r.id}>{getMood(r.mood)?.emoji}</span>)}
                </span>
                {recs.length > 0 && <span className="mm-day__count">{recs.length}</span>}
              </button>
            );
          })}
        </div>
      </section>

      {/* Selected day */}
      <section className="mm-panel">
        <h2 className="mm-panel__title">{selectedDate ? fmtLongDay(selectedDate) : 'Pick a day'}</h2>
        {selectedRecords.length ? (
          <CheckInList records={selectedRecords} />
        ) : (
          <p className="mm-empty-sub">No check-ins recorded on this day.</p>
        )}
      </section>

      {/* Distribution + summary */}
      <div className="mm-two">
        <section className="mm-panel">
          <h2 className="mm-panel__title">Mood distribution</h2>
          <p className="mm-panel__sub">{summarySentence(weekRecords, 'this week')}</p>
          <MoodDonut records={weekRecords} />
        </section>
        <section className="mm-panel">
          <h2 className="mm-panel__title">By mood</h2>
          <MoodBars records={weekRecords} />
        </section>
      </div>

      {/* Reflection */}
      <section className="mm-panel">
        <h2 className="mm-panel__title">Weekly reflection</h2>
        <p className="mm-panel__sub">Optional and private — separate from the summary above.</p>
        <ReflectionPanel period="week" periodKey={weekKey(anchor)} />
      </section>
    </div>
  );
};

export default WeeklyView;
